import {
  StopOutlined,
  MoreOutlined,
  CloudUploadOutlined,
  DownOutlined,
  LoadingOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import {
  clearPendingUploads,
  restoreUploadReceipts,
  cancelAllUploads,
} from "../../redux/Slices/MediaSlice";
import UploadCard from "./UploadCard/UploadCard";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import {
  Card,
  Tooltip,
  Badge,
  Progress,
  Flex,
  Typography,
  Button,
  Dropdown,
} from "antd";
import { useRef, useEffect, FC, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { dismissUploads } from "../../helpers/UploadLifetime";
import { useWindowFileDropGuard } from "../../hooks/useWindowFileDropGuard";
import { UPLOAD_TONE_FILL, uploadPanelTone } from "./uploadTone";
import "./UploadProgress.scss";

export const UploadProgress: FC = () => {
  const [minimized, setMinimized] = useState(false);
  const { pendingUploads } = useAppSelector((state) => state.media);
  const { folderById } = useAppSelector((state) => state.folder);
  const dispatch = useAppDispatch();
  const { t } = useTranslation("media");

  useEffect(() => {
    void dispatch(restoreUploadReceipts());
    const timer = setInterval(() => { void dispatch(restoreUploadReceipts()); }, 60000);
    return () => clearInterval(timer);
  }, [dispatch]);

  const finishedCount = useMemo(() => {
    return pendingUploads.filter((upload) => upload.status === "completed")
      .length;
  }, [pendingUploads]);

  const activeCount = useMemo(() => {
    return pendingUploads.filter(
      (upload) => upload.status === "uploading" || upload.status === "pending",
    ).length;
  }, [pendingUploads]);

  useWindowFileDropGuard(activeCount > 0);

  const totalProgress = useMemo(() => {
    if (pendingUploads.length === 0) return 0;
    const total = pendingUploads.reduce(
      (sum, upload) => sum + upload.progress,
      0,
    );
    return Math.round(total / pendingUploads.length);
  }, [pendingUploads]);

  useEffect(() => {
    if (finishedCount === pendingUploads.length && pendingUploads.length > 0) {
      const timer = setTimeout(() => setMinimized(true), 3000);
      return () => clearTimeout(timer);
    } else {
      return;
    }
  }, [finishedCount, pendingUploads.length]);

  const previousActiveCount = useRef(0);
  useEffect(() => {
    // A new upload started after the panel was minimized: bring it back.
    if (previousActiveCount.current === 0 && activeCount > 0) setMinimized(false);
    previousActiveCount.current = activeCount;
  }, [activeCount]);

  if (pendingUploads.length === 0) return null;

  const tone = uploadPanelTone(pendingUploads);
  const fill = UPLOAD_TONE_FILL[tone];
  const failedCount = pendingUploads.filter((u) => u.status === "failed").length;
  const cancelledCount = pendingUploads.filter((u) => u.status === "cancelled").length;
  const allSettled = pendingUploads.every(
    (upload) => upload.status !== "pending" && upload.status !== "uploading",
  );
  const counts = { done: finishedCount, total: pendingUploads.length };

  const handleCancelAll = () => {
    dispatch(cancelAllUploads());
  };

  const actionMenuItems = [
    {
      key: "cancelAll",
      label: t("upload.cancelAll"),
      icon: <StopOutlined />,
      onClick: handleCancelAll,
      danger: true,
    },
  ];

  const onClose = () => {
    // Mark exactly what clearPendingUploads removes, so reload recovery doesn't re-add it.
    dismissUploads(
      pendingUploads
        .filter(
          (u) =>
            !u.releasePending &&
            u.status !== "uploading" &&
            u.status !== "pending",
        )
        .map((u) => u.id),
    );
    dispatch(clearPendingUploads());
  };

  const activeBadge =
    activeCount > 0 ? (
      <Badge count={activeCount} size="small" className="st-upload-panel__badge" />
    ) : null;

  return (
    <div className="st-upload-panel">
      {minimized ? (
        <Tooltip title={t("upload.minimizedTooltip", counts)}>
          <Card
            className="st-upload-panel__bubble"
            role="button"
            tabIndex={0}
            aria-label={t("upload.expand")}
            onClick={() => setMinimized(false)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setMinimized(false);
              }
            }}
          >
            <span className="st-upload-panel__bubble-icon">
              <CloudUploadOutlined />
              {activeBadge}
            </span>
            <Progress
              className="st-meter"
              size={[40, 6]}
              percent={totalProgress}
              showInfo={false}
              strokeColor={fill}
            />
          </Card>
        </Tooltip>
      ) : (
        <Card className="st-upload-panel__card">
          <Flex justify="space-between" align="flex-start" gap={12}>
            <Flex align="center" gap={12}>
              <span
                className={`st-upload-panel__tile st-upload-panel__tile--${tone}`}
                aria-hidden="true"
              >
                <CloudUploadOutlined />
                {activeBadge}
              </span>
              <Flex vertical>
                <Typography.Title level={5} className="st-upload-panel__title">
                  {t("upload.title")}
                </Typography.Title>
                {folderById ? (
                  <Typography.Text type="secondary" className="st-upload-panel__line">
                    {t("upload.toFolder")} <strong>{folderById.name}</strong>
                  </Typography.Text>
                ) : null}
                <Typography.Text type="secondary" className="st-upload-panel__line st-tabular">
                  {t("upload.summary", counts)}
                </Typography.Text>
                {failedCount > 0 ? (
                  <Typography.Text type="danger" className="st-upload-panel__line st-tabular">
                    {failedCount} {t("upload.statusFailed")}
                  </Typography.Text>
                ) : null}
                {cancelledCount > 0 ? (
                  <Typography.Text type="secondary" className="st-upload-panel__line st-tabular">
                    {cancelledCount} {t("upload.statusCancelled")}
                  </Typography.Text>
                ) : null}
              </Flex>
            </Flex>

            <Flex gap={4}>
              {activeCount > 0 ? (
                <Dropdown
                  menu={{ items: actionMenuItems }}
                  placement="bottomRight"
                  trigger={["click"]}
                >
                  <Button
                    type="text"
                    size="small"
                    icon={<MoreOutlined />}
                    aria-label={t("upload.actions")}
                  />
                </Dropdown>
              ) : null}
              <Tooltip title={t("upload.minimize")}>
                <Button
                  type="text"
                  size="small"
                  icon={<DownOutlined />}
                  aria-label={t("upload.minimize")}
                  onClick={() => setMinimized(true)}
                />
              </Tooltip>
              {allSettled ? (
                <Tooltip title={t("common:actions.close")}>
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseOutlined />}
                    aria-label={t("common:actions.close")}
                    onClick={onClose}
                  />
                </Tooltip>
              ) : null}
            </Flex>
          </Flex>

          <div className="st-upload-panel__overall">
            <Flex justify="space-between" align="center">
              <Typography.Text strong>{t("upload.overallProgress")}</Typography.Text>
              <Typography.Text strong className="st-tabular">
                {totalProgress}%
              </Typography.Text>
            </Flex>
            <Progress
              className="st-meter"
              size={[-1, 6]}
              percent={totalProgress}
              showInfo={false}
              strokeColor={fill}
            />
          </div>

          <div className="st-upload-panel__list">
            {pendingUploads.map((uploadItem) => (
              <UploadCard key={uploadItem.id} uploadItem={uploadItem} />
            ))}
          </div>

          {activeCount > 0 && (
            <div className="st-upload-panel__footer">
              <Typography.Text type="secondary">
                <LoadingOutlined spin /> {t("upload.inProgress", { count: activeCount })}
              </Typography.Text>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};
